<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const defaults = {
	name: 'Author Name',
	title: 'Author Title',
	image: '/webclip.png',
	bio: '<p>Author biography and academic background.</p>',
	lastUpdated: '',
	books: [],
	amazonLink: '',
}
const schema = defineSchema({
	name: $.text('Name'),
	title: $.text('Title'),
	image: $.upload('Image'),
	bio: $.richtext('Bio', { basic: true }),
	lastUpdated: $.text('Last Updated'),
	books: $.array(
		'Books',
		defineSchema({
			title: $.text('Title'),
			image: $.upload('Image'),
			link: $.text('Link'),
		}),
		{ collapsible: true },
	),
	amazonLink: $.text('Amazon Link'),
})
export const BlockAuthorProfile = defineBlock({
	pickerHidden: true,
	thumbnail: '/block-thumbs/BlockAuthorProfile.svg',
	category: 'Content',
	order: 140,
	name: 'BlockAuthorProfile',
	label: 'Author Profile',
	icon: 'badge',
	schema,
	defaults,
})
</script>
<script setup lang="ts">

import { sanitizeArticleHtml } from '@/utils/articleHtml'
import { displayImage } from '@/utils/image'
import { Image } from '@bagelink/vue'
interface Book {
	title: string
	image: string
	link?: string
}

interface AuthorProfile {
	name: string
	title: string
	image: string
	bio: string
	lastUpdated: string
	books: Book[]
	amazonLink?: string
}

const props = defineProps<{
	authorProfile?: AuthorProfile
	name?: string
	title?: string
	image?: string
	bio?: string
	lastUpdated?: string
	books?: Book[]
	amazonLink?: string
}>()

// Support both nested authorProfile prop and flat props
const getName = () => props.authorProfile?.name || props.name || ''
const getTitle = () => props.authorProfile?.title || props.title || ''
const getImage = () => props.authorProfile?.image || props.image || ''
const getBio = () => props.authorProfile?.bio || props.bio || ''
const getLastUpdated = () => props.authorProfile?.lastUpdated || props.lastUpdated || ''
const getBooks = () => props.authorProfile?.books || props.books || []
const safeBookLink = (value?: string) => {
	if (!value) return undefined
	try {
		const url = new URL(value, 'https://www.thetorah.com')
		return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined
	} catch {
		return undefined
	}
}
</script>

<template>
	<div>
		<div class="flex gap-1 align-items-start pb-2 m_pe-1">
			<Image
				:src="displayImage(getImage(), 'thumb')"
				:alt="getName()"
				ratio="1"
				class="h115px m_h-60px aspect-ratio-1 round"
			/>
			<div>
				<h1 class="txt40 m_txt18 my-0 regular line-height-14 pt-1 m_pt-05">
					{{ getName() }}
				</h1>
				<p v-if="getTitle()" class="txt20 m_txt14 color-gray-70 my-0 light line-height-16">
					{{ getTitle() }}
				</p>
			</div>
		</div>

		<div class="m_pe-1">
			<div class="txt17 line-height-18 light m_txt15" v-html="sanitizeArticleHtml(getBio())" />
			<p v-if="getLastUpdated()" class="color-gray-70 txt14 pt-075">Last Updated: {{ getLastUpdated() }}</p>
		</div>

		<div v-if="getBooks().length > 0" class="author-books">
			<h3 class="txt30 regular my-0 pt-2 pb-075 m_txt22">Books by the Author</h3>
			<div
				class="grid grid-wrap-4 gap-2 book-cover-wrap m_overflow m_flex m_gap-1 m_ps-1 m_-ms-1 m_pb-1 m_pe-1"
			>
				<a
					v-for="book in getBooks()"
					:key="book.title"
					:href="safeBookLink(book.link)"
					target="_blank"
					rel="noopener"
					class="decoration-none m_min-w150px m_grid-shrink-0"
				>
					<Image :src="displayImage(book.image, 'card')" :alt="book.title" class="book-cover w100p cover mb-05" lazy />
					<p class="light">
						{{ book.title }}
					</p>
				</a>
			</div>
			<p class="txt12 color-gray-70 pt-1 pb-2 m_px-1">Amazon paid links</p>
		</div>
	</div>
</template>

<style>
.book-cover {
	aspect-ratio: 148/225;
}
@media (max-width: 910px) {
	.author-books {
		width: calc(100vw - 1rem);
	}
}
</style>
